'use client';

import { useEffect, useMemo, useState } from 'react';
import type {
  TransactionCategory,
  TransactionsViewQuery,
} from '@bts-web/data-layer/server';
import { useRouter } from 'next/navigation';
import { css } from '@bts-web/utils-style-engine';
import { TransactionsTranslationsKeysResult } from '../utils/getTransactionsTranslations';
import { getTransactions } from '../../common/gqlActions/getTransactions.action';
import { InfiniteTransactionList } from '../InfiniteTransactionList/InfiniteTransactionList';
import { TransactionsFilterHeader } from '../TransactionsFilterHeader';
import { useHandleGqlErrorsWithSnackbar } from '../../common/utils/errors';
import { CellSkeleton, NothingToShow } from '../../common/components';

export type TransactionsListTranslations = {
  activity: string;
  transactionDescription: string;
  amount: string;
};

export interface TransactionsListProps {
  itemsPerPage: number;
  assetId: string;
  translations: TransactionsTranslationsKeysResult;
  initialTransactions: TransactionsViewQuery['response']['transactions'];
  locale: string;
  hasCryptoTransfers?: boolean;
  hasStaking?: boolean;
}

export const InfiniteScrollTransactionListWrapper = ({
  itemsPerPage,
  assetId,
  translations,
  initialTransactions,
  locale,
  hasCryptoTransfers,
  hasStaking,
}: TransactionsListProps) => {
  const router = useRouter();

  const { processErrors } = useHandleGqlErrorsWithSnackbar();

  const [category, setCategory] = useState<TransactionCategory | undefined>(
    undefined,
  );

  const [loading, setLoading] = useState(false);

  const [transactions, setTransactions] =
    useState<TransactionsViewQuery['response']['transactions']>(
      initialTransactions,
    );

  useEffect(() => {
    getTransactions({
      input: {
        first: itemsPerPage,
        assetId,
      },
    })
      .then((transactionQueryData) => {
        const { data } = transactionQueryData;

        setTransactions(data.transactions);
      })
      .catch(() => {
        router.push('/error/server');
      });
  }, []);

  const currentEndCursor = useMemo(() => {
    return transactions?.pageInfo.endCursor;
  }, [transactions?.pageInfo.endCursor]);

  const refetchTransactions = async (newCategory: TransactionCategory) => {
    setLoading(true);

    const queryParams = {
      input: {
        first: itemsPerPage,
        category: newCategory,
        ...(assetId && { assetId }),
      },
    };

    try {
      const transactionQueryData = await getTransactions(queryParams);

      const { transactions: responseTransactions } =
        transactionQueryData?.data ?? {};

      if (transactionQueryData.errors && !responseTransactions) {
        processErrors(transactionQueryData.errors);

        setLoading(false);

        return;
      }

      setTransactions({
        ...responseTransactions,
        edges: responseTransactions?.edges || [],
      } as TransactionsViewQuery['response']['transactions']);

      setLoading(false);
    } catch (error) {
      if (typeof error === 'string') {
        processErrors([error] as string[]);
      } else {
        processErrors(error as unknown as string[]);
      }

      setLoading(false);
    }
  };

  const loadMoreTransactions = async () => {
    const queryParams = {
      input: {
        first: itemsPerPage,
        after: currentEndCursor,
        category,
        ...(assetId && { assetId }),
      },
    };

    try {
      const transactionQueryData = await getTransactions(queryParams);

      const { transactions: responseTransactions } =
        transactionQueryData?.data ?? {};

      if (transactionQueryData.errors && !responseTransactions) {
        processErrors(transactionQueryData.errors);

        return;
      }

      setTransactions({
        ...responseTransactions,
        edges: [
          ...(transactions?.edges || []),
          ...(responseTransactions?.edges || []),
        ],
      } as TransactionsViewQuery['response']['transactions']);
    } catch (error) {
      if (typeof error === 'string') {
        processErrors([error] as string[]);
      } else {
        processErrors(error as unknown as string[]);
      }
    }
  };

  const handleCategoryChange = (newCategory: TransactionCategory) => {
    setCategory(newCategory);

    refetchTransactions(newCategory);
  };

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: 'medium',
      })}
      data-testid="completed-transaction-list"
    >
      <TransactionsFilterHeader
        setCategory={handleCategoryChange}
        category={category}
        translations={translations}
        hasCryptoTransfers={hasCryptoTransfers}
        hasStaking={hasStaking}
      />
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
        })}
      >
        {loading ? (
          <>
            <CellSkeleton />
            <CellSkeleton />
          </>
        ) : !transactions ||
          !transactions.edges ||
          transactions?.edges?.length === 0 ? (
          <div
            data-testid="empty-transactions"
            className={css({ paddingTop: 'large' })}
          >
            <NothingToShow text={translations.emptyStateNoTransactions} />
          </div>
        ) : (
          <InfiniteTransactionList
            transactions={transactions}
            loadMoreTransactions={loadMoreTransactions}
            itemsPerPage={itemsPerPage}
            translations={translations}
            locale={locale}
          />
        )}
      </div>
    </div>
  );
};
